var React = require('react');
var { connect } = require('react-redux');
var farmingUnitManager = require('farmingUnitManager');
var dayjs = require('../../utils/dayjsConfig');
var distributionActions = require('distributionActions');
var CustomMap = require('CustomMap');
var { withTranslation } = require('react-i18next');
var {
  buildFarmingUnitNdviDayIndex,
  getFarmingUnitNdviForTimestamp,
  collapseNdviTilesByDay,
} = require('@managers/ndviManager');
var {
  filterToSelectedFieldTimestamps,
} = require('../general/ndviTimelineHelpers');
var {
  MAP_UPDATE,
  mapUpdateOnPropsChange,
  shouldDrawOnMount,
  routeFieldSelection,
} = require('./mapContainerHelpers');

/**
 * Extract unique timestamps from field-level fallback NDVI keys.
 * Keys are in format: "<timestamp>_<fieldId>"
 */
function extractTimestampsFromFieldLevelKeys(values) {
  const timestamps = {};
  for (const key in values) {
    const underscoreIndex = key.indexOf('_');
    if (underscoreIndex > 0) {
      const timestamp = key.substring(0, underscoreIndex);
      timestamps[timestamp] = true;
    }
  }
  return timestamps;
}

/**
 * Collect field tiles for a specific timestamp from field-level fallback data.
 */
function collectFieldTilesForTimestamp(farmingUnitValues, timestamp) {
  const tiles = [];
  for (const key in farmingUnitValues) {
    const underscoreIndex = key.indexOf('_');
    if (underscoreIndex > 0) {
      const ts = key.substring(0, underscoreIndex);
      if (ts === timestamp) {
        tiles.push(farmingUnitValues[key]);
      }
    }
  }
  return tiles;
}

/**
 * Build farming_details for a tile based on farming unit NDVI data.
 */
function buildFarmingDetails(farmingUnitNdvi, timestamp, isFieldLevelFallback) {
  if (!farmingUnitNdvi || !farmingUnitNdvi.values) {
    return null;
  }

  if (isFieldLevelFallback) {
    const tiles = collectFieldTilesForTimestamp(
      farmingUnitNdvi.values,
      timestamp,
    );
    if (tiles.length > 0) {
      return { isFieldLevel: true, tiles };
    }
  } else {
    // Match by day, not exact timestamp: the farming-unit image for a date can
    // be stored a few minutes off from the field tile it anchors to.
    const farmingUnitValue = getFarmingUnitNdviForTimestamp(
      farmingUnitNdvi.values,
      buildFarmingUnitNdviDayIndex(farmingUnitNdvi.values),
      timestamp,
    );
    if (farmingUnitValue) {
      return farmingUnitValue;
    }
  }

  return null;
}

/**
 * Create an NDVI tile object from farming unit data (used when no field NDVI exists).
 */
function createTileFromFarmingUnit(
  farmingUnitNdvi,
  timestamp,
  isFieldLevelFallback,
) {
  const tile = { isSelectedField: true };
  const farmingDetails = buildFarmingDetails(
    farmingUnitNdvi,
    timestamp,
    isFieldLevelFallback,
  );

  if (!farmingDetails) {
    return null;
  }

  if (
    isFieldLevelFallback &&
    farmingDetails.tiles &&
    farmingDetails.tiles.length > 0
  ) {
    const firstTile = farmingDetails.tiles[0];
    tile.image_path = firstTile.image_path;
    tile.bbox = firstTile.bbox;
    tile.min = firstTile.min;
    tile.max = firstTile.max;
  } else if (!isFieldLevelFallback) {
    tile.image_path = farmingDetails.image_path;
    tile.bbox = farmingDetails.bbox;
    tile.min = farmingDetails.min;
    tile.max = farmingDetails.max;
  }

  tile.farming_details = farmingDetails;
  return tile.image_path ? tile : null;
}

class MapContainer extends React.Component {
  constructor(props) {
    super(props);

    this.onPolygonClick = this.onPolygonClick.bind(this);
    this.onMarkerClick = this.onMarkerClick.bind(this);
    this.updateMap = this.updateMap.bind(this);
    this.screenResize = this.screenResize.bind(this);
    this.onLayerSelection = this.onLayerSelection.bind(this);
    this.onNDVILayerSelection = this.onNDVILayerSelection.bind(this);
  }

  componentDidMount() {
    if (shouldDrawOnMount(this.props)) {
      this.updateMap(this.props.selectedField);
    }

    window.addEventListener('resize', this.screenResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.screenResize);
  }

  componentDidUpdate(prevProps, prevState) {
    // `hidden` (Farm View Field details tab on the Farm map tab): the map sits
    // in a display:none box, so it is 0x0 and Leaflet's fit-bounds math yields
    // NaN. Skip redraws while hidden and do one full update (it starts with
    // invalidateSize) when it becomes visible again (mapContainerHelpers.js).
    const mapUpdate = mapUpdateOnPropsChange({
      hidden: this.props.hidden,
      wasHidden: prevProps.hidden,
    });
    if (mapUpdate === MAP_UPDATE.SKIP) {
      return;
    }
    if (mapUpdate === MAP_UPDATE.FULL) {
      this.updateMap(this.props.selectedField);
      return;
    }

    const prevFieldId = prevProps.selectedField?.id;
    const currentFieldId = this.props.selectedField?.id;
    const fieldSelected = Boolean(currentFieldId && !prevFieldId);
    const fieldDeselected = Boolean(!currentFieldId && prevFieldId);
    const fieldChanged = prevFieldId !== currentFieldId;
    const ndviDataJustArrived =
      !prevProps.selectedField?.historical_data?.ndvi &&
      this.props.selectedField?.historical_data?.ndvi;
    const farmingNdviJustArrived =
      !prevProps.selectedFarmingUnit?.historical_data?.ndvi &&
      this.props.selectedFarmingUnit?.historical_data?.ndvi;
    const stressDataJustArrived =
      !prevProps.selectedField?.historical_data?.adjusted_stress &&
      this.props.selectedField?.historical_data?.adjusted_stress;
    const fieldHistoryJustSucceeded =
      this.props.loadHistoryStatus?.status === 'success' &&
      prevProps.loadHistoryStatus?.status !== 'success';
    const farmingHistoryJustSucceeded =
      this.props.farmingHistoryStatus?.status === 'success' &&
      prevProps.farmingHistoryStatus?.status !== 'success';
    const archiveModeChanged = prevProps.archiveMode !== this.props.archiveMode;

    if (
      ndviDataJustArrived ||
      farmingNdviJustArrived ||
      stressDataJustArrived ||
      fieldHistoryJustSucceeded ||
      farmingHistoryJustSucceeded ||
      fieldSelected ||
      fieldDeselected ||
      fieldChanged ||
      archiveModeChanged
    ) {
      this.updateMap(this.props.selectedField);
    }
  }

  screenResize() {
    this.setState({});
  }

  /**
   * Get reference timestamps for NDVI display.
   * Uses selected field's timestamps if available, otherwise falls back to farming unit timestamps.
   */
  getReferenceTimestamps(
    selectedNDVITiles,
    farmingUnitNdvi,
    isFieldLevelFallback,
  ) {
    if (Object.keys(selectedNDVITiles).length > 0) {
      return selectedNDVITiles;
    }

    if (!farmingUnitNdvi?.values) {
      return {};
    }

    if (isFieldLevelFallback) {
      return extractTimestampsFromFieldLevelKeys(farmingUnitNdvi.values);
    }

    return farmingUnitNdvi.values;
  }

  /**
   * Process a single field's NDVI tiles and add them to the polygons collection.
   */
  processFieldNdviTiles(
    fieldToCheck,
    field,
    referenceTimestamps,
    farmingUnitNdvi,
    isFieldLevelFallback,
    ndviPolygons,
  ) {
    if (
      !fieldToCheck?.historical_data ||
      !('ndvi' in fieldToCheck.historical_data)
    ) {
      return false;
    }

    var isSelectedField = fieldToCheck.id === field?.id;
    var tiles = fieldToCheck.historical_data['ndvi']['values'];

    for (var timestamp in tiles) {
      if (!(timestamp in referenceTimestamps)) {
        continue;
      }

      if (!tiles[timestamp]) {
        continue;
      }

      var tile = JSON.parse(JSON.stringify(tiles[timestamp]));

      if (tile['image_path']) {
        tile['isSelectedField'] = isSelectedField;

        if (isSelectedField) {
          var farmingDetails = buildFarmingDetails(
            farmingUnitNdvi,
            timestamp,
            isFieldLevelFallback,
          );
          if (farmingDetails) {
            tile['farming_details'] = farmingDetails;
          }
        }
      } else {
        tile = {
          tile: tiles[timestamp],
          mean: undefined,
          cloudiness: undefined,
          useful_area: undefined,
          source: undefined,
        };
      }

      if (timestamp in ndviPolygons[0]) {
        ndviPolygons[0][timestamp].push(tile);
      } else {
        ndviPolygons[0][timestamp] = [tile];
      }
    }

    return true;
  }

  /**
   * Build NDVI polygons data for the map from field and farming unit data.
   */
  buildNdviPolygons(field) {
    var ndviPolygons = [{}];
    var farmingUnit = this.props.selectedFarmingUnit;

    // Skeleton farming units omit `fields` until the full tree loads.
    if (!farmingUnit?.fields) {
      return ndviPolygons;
    }

    var selectedNDVITiles = {};
    if (field?.historical_data && 'ndvi' in field.historical_data) {
      selectedNDVITiles = field.historical_data['ndvi']['values'];
    }

    var farmingUnitNdvi = farmingUnit.historical_data?.['ndvi'];
    var isFieldLevelFallback = farmingUnitNdvi?.source === 'field_level';
    var referenceTimestamps = this.getReferenceTimestamps(
      selectedNDVITiles,
      farmingUnitNdvi,
      isFieldLevelFallback,
    );

    // Process NDVI tiles from all fields
    var fieldsWithNdvi = 0;
    for (var fieldId in farmingUnit.fields) {
      var currentField = farmingUnit.fields[fieldId];
      var isSelected = currentField.id === field?.id;
      var fieldToCheck = isSelected ? field : currentField;

      var hasNdvi = this.processFieldNdviTiles(
        fieldToCheck,
        field,
        referenceTimestamps,
        farmingUnitNdvi,
        isFieldLevelFallback,
        ndviPolygons,
      );
      if (hasNdvi) fieldsWithNdvi++;
    }

    // Fallback: create tiles from farming unit NDVI when no fields have NDVI data
    if (fieldsWithNdvi === 0 && Object.keys(referenceTimestamps).length > 0) {
      for (var timestamp in referenceTimestamps) {
        var tile = createTileFromFarmingUnit(
          farmingUnitNdvi,
          timestamp,
          isFieldLevelFallback,
        );
        if (tile) {
          ndviPolygons[0][timestamp] = [tile];
        }
      }
    }

    // Collapse to one entry per day: captures from the same satellite pass can
    // land under near-but-not-equal timestamps, which would otherwise show as
    // duplicate rows for one date in the timeline.
    ndviPolygons[0] = collapseNdviTilesByDay(ndviPolygons[0]);

    // Hide timeline dates that have no image for the selected field. On a
    // multi-tile farming unit a field only has imagery on the dates its own
    // Sentinel-2 tile was captured, but the unit-wide timeline listed every
    // date, leaving empty rows for this field. The helper no-ops when nothing
    // is field-selected or the field has no field-level imagery, preserving the
    // farming-unit timeline for those cases.
    ndviPolygons[0] = filterToSelectedFieldTimestamps(ndviPolygons[0]);

    return ndviPolygons;
  }

  updateMap(field) {
    // Skeleton farming units have no `fields` — area/marker builders would
    // iterate undefined. Bail until the full distribution tree lands.
    if (this.props.selectedFarmingUnit?.fields) {
      var selected_field_index = field ? field?.id : '';

      if (this.custom_map) {
        this.custom_map.currentFarmingUnitId =
          this.props.selectedFarmingUnit?.id ?? '';

        let fieldsState = this.props.archiveMode ? 'archive' : 'active';
        var all_areas = farmingUnitManager.all_farming_unit_areas(
          this.props.selectedFarmingUnit,
          selected_field_index,
          fieldsState,
        );

        // display markers only when we display all sensors
        var all_markers = {};
        if (
          this.props.selectedFarmingUnit &&
          farmingUnitManager.shouldDisplayAllSensors(
            this.props.selectedFarmingUnit,
          )
        ) {
          all_markers = farmingUnitManager.all_farming_unit_markers(
            this.props.selectedFarmingUnit,
            selected_field_index,
            fieldsState,
          );
        }

        var ndviPolygons = this.buildNdviPolygons(field);

        // Always show "Selected field only" toggle in Field View
        this.custom_map.invalidate(
          all_areas,
          all_markers,
          ndviPolygons,
          false,
          true,
        );
      }
    }
  }

  onPolygonClick(polygon_id) {
    var currentSelectedField =
      this.props.selectedFarmingUnit?.fields?.[polygon_id];
    if (!currentSelectedField) {
      return;
    }

    this.selectField(currentSelectedField);
  }

  // Farm View's Field details tab passes `onFieldSelect` so its own selection
  // state stays in sync; the Field View just updates Redux.
  selectField(field) {
    routeFieldSelection(this.props, field);
  }

  onMarkerClick(polygon_id) {
    var currentSelectedField =
      this.props.selectedFarmingUnit?.fields?.[polygon_id];

    if (
      !currentSelectedField ||
      this.props.selectedField?.id === currentSelectedField.id
    ) {
      return;
    }

    this.selectField(currentSelectedField);
  }

  onLayerSelection(layerName) {
    mixpanel.track('Map Layer Change', { 'Layer Name': layerName });
  }

  onNDVILayerSelection(layerName, sourceLabel) {
    let date = dayjs.unix(layerName).format('DD-MM-YYYY');
    let imageDate = dayjs.unix(layerName);
    let today = dayjs();
    let ageInDays = today.diff(imageDate, 'days');

    mixpanel.track('NDVI Layer Change', {
      'Layer Name': layerName,
      Date: date,
      Source: sourceLabel || 'Unknown',
      'Age in Days': ageInDays,
    });
  }

  render() {
    const { t } = this.props;
    var is_admin =
      this.props.selectedFarmingUnit &&
      this.props.selectedFarmingUnit.role == 'admin';

    let mapHeight = '664';
    if (window.innerWidth < 768) {
      mapHeight = '400';
    }

    // Embedded (Farm View Field details tab): just the map, sized by the host.
    if (this.props.embedded) {
      return (
        <CustomMap
          mapId='dashboard'
          showEditToolBar={false}
          onRef={(ref) => (this.custom_map = ref)}
          message=''
          onPolygonClick={this.onPolygonClick}
          onMarkerClick={this.onMarkerClick}
          onLayerSelection={this.onLayerSelection}
          onNDVILayerSelection={this.onNDVILayerSelection}
          map_height={this.props.mapHeight || '650'}
          shouldPresentStressMenu={true}
          shouldPresentFilePicker={false}
          shouldDisplayNDVI={true}
        />
      );
    }

    return (
      <div
        className='w-100 pb-lg-5 pb-0'
        style={{
          backgroundColor: '#F6F8FA',
          borderRight: '1px solid #DCE0E8',
          borderLeft: '1px solid #DCE0E8',
          borderBottom: '1px solid #DCE0E8',
          borderBottomRightRadius: '8px',
          borderBottomLeftRadius: '8px',
          zIndex: '0',
        }}
      >
        <div className='d-lg-none text-grey45 text-size-16 pb-3 font-weight-semi-bold pl-4'>
          {t('map_view')}
        </div>
        <div className='pl-3 pr-3' style={{ paddingTop: '40px' }}>
          <CustomMap
            mapId='dashboard'
            showEditToolBar={false}
            onRef={(ref) => (this.custom_map = ref)}
            message=''
            onPolygonClick={this.onPolygonClick}
            onMarkerClick={this.onMarkerClick}
            onLayerSelection={this.onLayerSelection}
            onNDVILayerSelection={this.onNDVILayerSelection}
            map_height={mapHeight}
            shouldPresentStressMenu={true}
            shouldPresentFilePicker={false}
            shouldDisplayNDVI={true}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = function (state) {
  return {
    selectedField: state.distribution_data.selected_entities.field,
    selectedFarmingUnit: state.distribution_data.selected_entities.farming_unit,
    loadHistoryStatus: state.distribution_data.field_load_history,
    farmingHistoryStatus: state.distribution_data.farming_unit_load_history,
    archiveMode: state.distribution_data.field_state === 'archive',
  };
};

const mapDispatchToProps = {
  setSelectedEntities: distributionActions.selected_entities,
};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(MapContainer));
